import { useState, useEffect, type FC } from "react";
import type {
  InvoiceRowModel,
  InvoiceRowValidationModel,
} from "../../../../models/InvoiceModels";
import { Col, Row } from "react-grid-system";
import Subheading from "../../../atoms/typography/Subheading";
import { useTranslation } from "react-i18next";
import { HorizontalSpacer } from "../../../atoms/Spacers";
import OutlinedButton from "../../../atoms/buttons/OutlinedButton";
import { theme } from "../../../../theme";
import { EditIcon } from "../../../atoms/icons/EditIcon";
import Small from "../../../atoms/typography/Small";
import styled from "styled-components";
import DeleteModal from "../../../molecules/modals/DeleteModal";
import TextField from "../../../atoms/inputs/TextField";
import CircleButton from "../../../atoms/buttons/CircleButton";
import { PlusIcon } from "../../../atoms/icons/PlusIcon";
import { useUpdateInvoiceRows } from "../../../../graphql/hooks/invoices/useUpdateInvoiceRows";
import BodySmall from "../../../atoms/typography/BodySmall";
import IconButton from "../../../atoms/buttons/IconButton";
import { SaveIconMedium } from "../../../atoms/icons/SaveIcon";
import Alert from "../../../molecules/Alert";
import Modal from "../../../molecules/Modal";
import { CheckmarkIconSmall } from "../../../atoms/icons/CheckmarkIcons";
import { CrossIcon } from "../../../atoms/icons/CrossIcon";
import Tooltip from "../../../molecules/Tooltip";
import { useRegisterSaveSection } from "../SaveSectionsContext";
import { useDeleteInvoiceRows } from "../../../../graphql/hooks/invoices/useDeleteInvoiceRows";
import ChevronButton from "../../../atoms/buttons/ChevronButtons";
import { SlideOutHeight } from "../../../atoms/SlideOutHeight";

const InvoiceRowContentContainer = styled.div`
  padding: 8px;
  border-radius: 12px;
  background-color: ${theme.colors.grey02};
`;

const InvoiceRowDeletedTag = styled.div`
  position: fixed;
  right: 0;
  bottom: 200px;
  background-color: ${theme.colors.success20};
  padding: 0px 8px;
  border-top-left-radius: 24px;
  border-bottom-left-radius: 24px;
  display: flex;
  gap: 4px;
  align-items: center;
`;

type UpdateInvoiceRowsInput = {
  articleNumber: string;
  description: string;
  discountAmount: string | number;
  discountPercentage: string | number;
  invoiceRowId: string;
  quantity: string | number;
  totalWithoutVAT: string | number;
  unitPrice: string | number;
  unitType: string;
  vatPercentage: string | number;
};

type FieldErrors = Record<
  string,
  Partial<Record<keyof UpdateInvoiceRowsInput, string>>
>;

const fieldTypeToKey: Record<string, keyof UpdateInvoiceRowsInput> = {
  ArticleNumber: "articleNumber",
  Description: "description",
  DiscountAmount: "discountAmount",
  DiscountPercentage: "discountPercentage",
  ItemAmountExcludingVat: "totalWithoutVAT",
  Quantity: "quantity",
  RowTotalCalculation: "totalWithoutVAT",
  TotalWithoutVAT: "totalWithoutVAT",
  UnitPrice: "unitPrice",
  UnitCode: "unitType",
  UnitType: "unitType",
  VatPercentage: "vatPercentage",
};

const toFloat = (v: string | number | undefined): number =>
  typeof v === "number" ? v : parseFloat(v || "0") || 0;

const calculateRowTotalWithoutVat = (
  row: Pick<
    UpdateInvoiceRowsInput,
    "quantity" | "unitPrice" | "discountPercentage" | "discountAmount"
  >,
): string =>
  (
    toFloat(row.quantity) *
      toFloat(row.unitPrice) *
      (1 - toFloat(row.discountPercentage) / 100) -
    toFloat(row.discountAmount)
  ).toFixed(2);

const fieldsAffectingRowTotal: Array<keyof UpdateInvoiceRowsInput> = [
  "quantity",
  "unitPrice",
  "discountAmount",
  "discountPercentage",
];

const makeEmptyRow = (): UpdateInvoiceRowsInput => ({
  invoiceRowId: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
  articleNumber: "",
  description: "",
  discountAmount: "0",
  discountPercentage: "0",
  quantity: "0",
  totalWithoutVAT: "0",
  unitPrice: "0",
  unitType: "",
  vatPercentage: "0",
});

interface Props {
  invoiceRowsValidation: InvoiceRowValidationModel[];
  invoiceId: string;
  totalAmountWithVat: number;
  rounding: number;
  /** Net (excluding VAT) — Fg.Invoice stores ShippingFeeAmount as a net amount. */
  shippingCost: number;
  /** VAT rate applied to shippingCost, e.g. 25 for 25%. */
  shippingFeeVatPercentage: number;
  /** Added to the invoice total as-is; no VAT is calculated on it. */
  invoiceFee: number;
  invoiceRows: Array<InvoiceRowModel>;
  isEditable: boolean;
}

const InvoiceAmountSummaryContainer = styled.div`
  border: 1px solid ${theme.colors.grey05};
  border-radius: 12px;
  padding: 16px;
`;
const SummaryRow = styled(Row)<{ equal?: boolean }>`
  padding: 8px;
  border-bottom: ${theme.colors.grey05} 1px solid;
  background-color: ${(props) =>
    props.equal
      ? theme.colors.success20
      : props.equal === false
        ? theme.colors.error20
        : "transparent"};
`;

const InvoiceRowsDetailsSection: FC<Props> = ({
  invoiceRows,
  invoiceId,
  totalAmountWithVat,
  rounding,
  shippingCost,
  shippingFeeVatPercentage,
  invoiceFee,
  invoiceRowsValidation,
  isEditable,
}) => {
  const { t } = useTranslation();
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [addVatToAllFieldsModalOpen, setAddVatToAllFieldsModalOpen] =
    useState(false);
  const [newVatPercentage, setNewVatPercentage] = useState<number | undefined>(
    undefined,
  );
  const [rows, setRows] = useState<UpdateInvoiceRowsInput[]>([]);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [serverRowIds, setServerRowIds] = useState<Set<string>>(new Set());
  const [dataHasBeenUpdated, setDataHasBeenUpdated] = useState(false);
  const [deleteSuccesful, setDeleteSuccessful] = useState(false);
  const [open, setOpen] = useState(true);

  useEffect(() => {
    const initialRows: UpdateInvoiceRowsInput[] = invoiceRows.map((row, i) => {
      const rowValidation = invoiceRowsValidation[i];
      return {
        invoiceRowId:
          row.invoiceRowId ??
          rowValidation?.invoiceRowId ??
          `${Date.now()}-${i}`,
        articleNumber: row?.articleNumber ?? "",
        description: row?.description ?? "",
        quantity: String(row?.quantity ?? "0"),
        unitPrice: String(row?.unitPrice ?? "0"),
        unitType: row?.unitType ?? "",
        discountPercentage: String(row?.discountPercentage ?? "0"),
        discountAmount: String(row?.discountAmount ?? "0"),
        totalWithoutVAT: String(row?.totalWithoutVAT ?? "0"),
        vatPercentage: String(row?.vatPercentage ?? "0"),
      };
    });
    setRows(initialRows);
    setServerRowIds(
      new Set([
        ...invoiceRows.flatMap((r) => (r.invoiceRowId ? [r.invoiceRowId] : [])),
        ...invoiceRowsValidation.map((r) => r.invoiceRowId),
      ]),
    );

    const errors: FieldErrors = {};
    invoiceRowsValidation.forEach((rowValidation) => {
      rowValidation.rowFieldValidations.forEach((field) => {
        if (!field.isValid) {
          const key = fieldTypeToKey[field.fieldType];
          if (!key) return;
          if (!errors[rowValidation.invoiceRowId])
            errors[rowValidation.invoiceRowId] = {};
          errors[rowValidation.invoiceRowId][key] = t(
            "invoices.fieldIsInvalid",
            {
              fieldName: t(`invoices.${field.fieldType}`),
            },
          );
        }
      });
    });
    setFieldErrors(errors);
  }, [invoiceRowsValidation, invoiceRows]);

  // Derived summary calculations
  const rowsExVatTotal = rows.reduce(
    (sum, row) => sum + toFloat(row.totalWithoutVAT),
    0,
  );
  const rowsIncVatTotal = rows.reduce((sum, row) => {
    const exVat = toFloat(row.totalWithoutVAT);
    return sum + exVat * (1 + toFloat(row.vatPercentage) / 100);
  }, 0);
  const rowsVatTotal = rowsIncVatTotal - rowsExVatTotal;

  const shippingVatAmount = shippingCost * (shippingFeeVatPercentage / 100);
  const calculatedTotal =
    rowsIncVatTotal + shippingCost + shippingVatAmount + invoiceFee + rounding;
  const totalIncVatAndRowsTotalIsEqual =
    rows.length > 0 && Math.abs(calculatedTotal - totalAmountWithVat) < 0.01;

  const onCompletedUpdateRows = (res: any) => {
    if (res?.invoice?.updateInvoiceRows?.success) {
      setErrorMessage("");
      setSaveSuccessful(true);
      setTimeout(() => setSaveSuccessful(false), 5000);
      setDataHasBeenUpdated(false);
    } else {
      setErrorMessage(t("invoices.saveFailed"));
    }
  };
  const onErrorUpdateRows = (e: any) => {
    console.error(e);
    setErrorMessage(e?.toString() ?? t("invoices.saveFailed"));
  };
  const { updateInvoiceRows, loading, error } = useUpdateInvoiceRows({
    onCompleted: onCompletedUpdateRows,
    onError: onErrorUpdateRows,
  });

  const onClickSaveSection = async () => {
    const serializedRows = rows.map((row) => ({
      ...row,
      invoiceRowId: serverRowIds.has(row.invoiceRowId)
        ? row.invoiceRowId
        : null,
      discountAmount: toFloat(row.discountAmount),
      discountPercentage: toFloat(row.discountPercentage),
      quantity: toFloat(row.quantity),
      totalWithoutVAT: toFloat(row.totalWithoutVAT),
      unitPrice: toFloat(row.unitPrice),
      vatPercentage: toFloat(row.vatPercentage),
    }));
    try {
      await updateInvoiceRows({
        variables: {
          input: { invoiceId, rows: serializedRows },
        },
      });
    } catch (e) {
      console.error(e);
      setErrorMessage(e?.toString() ?? t("invoices.saveFailed"));
    }
  };

  useRegisterSaveSection({
    id: "invoiceRows",
    label: t("invoiceValidation.saveInvoiceRowDetails"),
    isDirty: dataHasBeenUpdated,
    onSave: onClickSaveSection,
    loading,
  });

  const onEditRowField = (
    rowId: string,
    field: keyof UpdateInvoiceRowsInput,
    value: string,
  ) => {
    if (!dataHasBeenUpdated) setDataHasBeenUpdated(true);

    setRows((prev) =>
      prev.map((row) => {
        if (row.invoiceRowId !== rowId) return row;

        const updatedRow = { ...row, [field]: value } as UpdateInvoiceRowsInput;
        if (fieldsAffectingRowTotal.includes(field)) {
          updatedRow.totalWithoutVAT = calculateRowTotalWithoutVat(updatedRow);
        }
        return updatedRow;
      }),
    );

    setFieldErrors((prev) => {
      if (!prev[rowId]?.[field]) return prev;
      const rowErrors = { ...prev[rowId] };
      delete rowErrors[field];
      return { ...prev, [rowId]: rowErrors };
    });
  };

  const onCompletedDeleteInvoiceRows = (result?: any) => {
    if (result?.invoice?.deleteInvoiceRows?.success) {
      setErrorMessage("");
      setDataHasBeenUpdated(false);
      setDeleteSuccessful(true);
      setTimeout(() => setDeleteSuccessful(false), 3000);
    } else {
      setErrorMessage(t("invoices.couldNotDeleteInvoiceRows"));
    }
  };
  const onErrorDeleteInvoiceRows = (e?: any) => {
    console.error(e);
    setErrorMessage(e?.toString() ?? t("invoices.couldNotDeleteInvoiceRows"));
  };
  const { deleteInvoiceRows: deleteSingleRow } = useDeleteInvoiceRows({
    onCompleted: onCompletedDeleteInvoiceRows,
    onError: onErrorDeleteInvoiceRows,
    noRefetch: true,
  });
  const { deleteInvoiceRows: deleteAllSavedRows, loading: deleteAllLoading } =
    useDeleteInvoiceRows({
      onCompleted: onCompletedDeleteInvoiceRows,
      onError: onErrorDeleteInvoiceRows,
      noRefetch: false,
    });

  const onClickAddNewRow = () => {
    if (!dataHasBeenUpdated) setDataHasBeenUpdated(true);

    setRows((prev) => [...prev, makeEmptyRow()]);
  };

  const onClickRemoveRow = async (rowId: string) => {
    setRows((prev) => prev.filter((r) => r.invoiceRowId !== rowId));
    setFieldErrors((prev) => {
      const updated = { ...prev };
      delete updated[rowId];
      return updated;
    });

    if (serverRowIds.has(rowId)) {
      try {
        await deleteSingleRow({
          variables: {
            input: { invoiceId, invoiceRowIds: [rowId] },
          },
        });
      } catch (e) {
        console.error(e);
        setErrorMessage(
          e?.toString() ?? t("invoices.couldNotDeleteInvoiceRows"),
        );
      }
    }
  };

  const onClickRemoveRows = async () => {
    const savedRowIds = rows
      .filter((r) => serverRowIds.has(r.invoiceRowId))
      .map((r) => r.invoiceRowId);

    setRows([]);
    setFieldErrors({});

    if (savedRowIds.length > 0) {
      try {
        await deleteAllSavedRows({
          variables: {
            input: { invoiceId, invoiceRowIds: savedRowIds },
          },
        });
      } catch (e) {
        console.error(e);
        setErrorMessage(
          e?.toString() ?? t("invoices.couldNotDeleteInvoiceRows"),
        );
      }
    } else {
      setDataHasBeenUpdated(false);
    }
  };

  const onClickAddVatToAllRows = (defaultVat: number) => {
    setNewVatPercentage(defaultVat);
    setAddVatToAllFieldsModalOpen(true);
  };

  const onUpdateVatOnAllRows = () => {
    if (newVatPercentage !== undefined) {
      setRows((prev) =>
        prev.map((row) => ({
          ...row,
          vatPercentage: String(newVatPercentage),
        })),
      );
      setDataHasBeenUpdated(true);
    }
    setAddVatToAllFieldsModalOpen(false);
  };

  return (
    <div
      style={{
        backgroundColor: dataHasBeenUpdated
          ? theme.colors.info05
          : "transparent",
        padding: 8,
        borderRadius: 4,
      }}
    >
      <Row nogutter justify="between">
        <Subheading>{t("invoices.invoiceRows")}</Subheading>
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <ChevronButton
            type={open ? "up" : "down"}
            onClick={() => setOpen(!open)}
          />
          {saveSuccessful ? (
            <BodySmall fontweight={500} color={theme.colors.success}>
              {t("random.saved")}
            </BodySmall>
          ) : (
            <Tooltip text={t("invoiceValidation.saveSection")}>
              <IconButton
                icon={
                  <SaveIconMedium
                    color={
                      dataHasBeenUpdated
                        ? theme.colors.white
                        : theme.colors.text
                    }
                  />
                }
                onClick={onClickSaveSection}
                size="medium"
                loading={loading}
                color={
                  dataHasBeenUpdated ? theme.colors.info : theme.colors.info05
                }
                disabled={!isEditable}
              />
            </Tooltip>
          )}
        </div>
      </Row>
      <SlideOutHeight isVisible={open}>
        <Row nogutter justify="between" align="center">
          {(error || errorMessage) && (
            <Alert
              type="error"
              title={t("invoices.errorSavingInvoiceRows")}
              message={errorMessage || error?.message}
            />
          )}
          <HorizontalSpacer spacing={1} />
          <Row
            nogutter
            align="center"
            justify="between"
            style={{ flexGrow: 1, flexShrink: 1, gap: 8 }}
          >
            {isEditable && (
              <DeleteModal
                deleteButtonType="outlinedButtonSmall"
                title={t("invoices.removeRows")}
                onClickDelete={onClickRemoveRows}
                message={t("invoices.withoutRowsInvoiceCantBeSentAsEInvoice")}
                loading={deleteAllLoading}
              />
            )}
            <OutlinedButton
              color={theme.colors.text}
              title={t("invoices.addVatToAllRows")}
              icon={<EditIcon />}
              onClick={() => onClickAddVatToAllRows(25)}
              disabled={!isEditable}
            />
          </Row>
        </Row>

        {rows.length > 0 && (
          <div>
            <HorizontalSpacer spacing={1} />
            <InvoiceAmountSummaryContainer>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <BodySmall>
                  {t("invoiceValidation.summaryOfInvoiceTotals")}
                </BodySmall>
                {totalIncVatAndRowsTotalIsEqual ? (
                  <CheckmarkIconSmall color={theme.colors.success} />
                ) : (
                  <CrossIcon color={theme.colors.error} />
                )}
              </div>
              <HorizontalSpacer spacing={0.5} />
              <SummaryRow
                nogutter
                justify="between"
                equal={totalIncVatAndRowsTotalIsEqual}
              >
                <BodySmall fontweight={500}>
                  {t("invoiceValidation.amountToPay")}
                </BodySmall>
                <BodySmall fontweight={500}>
                  {totalAmountWithVat.toFixed(2)} kr
                </BodySmall>
              </SummaryRow>
              <SummaryRow nogutter justify="between">
                <BodySmall>
                  {t("invoiceValidation.totalRowAmountExcludingVat")}
                </BodySmall>
                <BodySmall>{rowsExVatTotal.toFixed(2)} kr</BodySmall>
              </SummaryRow>
              <SummaryRow nogutter justify="between">
                <BodySmall>
                  {t("invoiceValidation.totalRowVatAmount")}
                </BodySmall>
                <BodySmall>{rowsVatTotal.toFixed(2)} kr</BodySmall>
              </SummaryRow>
              <SummaryRow nogutter justify="between">
                <BodySmall>
                  {t("invoices.shippingFeeAmountExcludingVat")}
                </BodySmall>
                <BodySmall>{shippingCost.toFixed(2)} kr</BodySmall>
              </SummaryRow>
              <SummaryRow nogutter justify="between">
                <BodySmall>
                  {t("invoiceValidation.shippingFeeVatAmount", {
                    percentage: shippingFeeVatPercentage,
                  })}
                </BodySmall>
                <BodySmall>{shippingVatAmount.toFixed(2)} kr</BodySmall>
              </SummaryRow>
              <SummaryRow nogutter justify="between">
                <BodySmall>{t("invoices.InvoiceFeeAmount")}</BodySmall>
                <BodySmall>{invoiceFee.toFixed(2)} kr</BodySmall>
              </SummaryRow>
              <SummaryRow nogutter justify="between">
                <BodySmall>{t("invoices.roundOff")}</BodySmall>
                <BodySmall>{rounding.toFixed(2)} kr</BodySmall>
              </SummaryRow>
              <SummaryRow
                nogutter
                justify="between"
                equal={totalIncVatAndRowsTotalIsEqual}
              >
                <BodySmall fontweight={500}>
                  {t("invoiceValidation.totalCalculatedIncludingVat")}
                </BodySmall>
                <BodySmall fontweight={500}>
                  {calculatedTotal.toFixed(2)} kr
                </BodySmall>
              </SummaryRow>
            </InvoiceAmountSummaryContainer>
            <HorizontalSpacer spacing={1} />
          </div>
        )}

        {rows.map((row) => (
          <div key={row.invoiceRowId}>
            <InvoiceRowContentContainer>
              <Row nogutter justify="end">
                {isEditable && (
                  <DeleteModal
                    deleteButtonType="circleButton"
                    onClickDelete={() => onClickRemoveRow(row.invoiceRowId)}
                  />
                )}
              </Row>
              <Row>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.articleNumber")}
                    fullWidth
                    value={row.articleNumber}
                    errorMessage={fieldErrors[row.invoiceRowId]?.articleNumber}
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "articleNumber", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.description")}
                    fullWidth
                    value={row.description}
                    errorMessage={fieldErrors[row.invoiceRowId]?.description}
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "description", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.numberOfAmount")}
                    fullWidth
                    type="number"
                    value={row.quantity}
                    errorMessage={fieldErrors[row.invoiceRowId]?.quantity}
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "quantity", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.unitPriceExcludingVat")}
                    fullWidth
                    type="number"
                    value={row.unitPrice}
                    errorMessage={fieldErrors[row.invoiceRowId]?.unitPrice}
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "unitPrice", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={12} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.unit")}
                    fullWidth
                    value={row.unitType}
                    errorMessage={fieldErrors[row.invoiceRowId]?.unitType}
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "unitType", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.discountPercentage")}
                    fullWidth
                    type="number"
                    value={row.discountPercentage}
                    errorMessage={
                      fieldErrors[row.invoiceRowId]?.discountPercentage
                    }
                    onChange={(value) =>
                      onEditRowField(
                        row.invoiceRowId,
                        "discountPercentage",
                        value,
                      )
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.discountAmount")}
                    fullWidth
                    type="number"
                    value={row.discountAmount}
                    errorMessage={fieldErrors[row.invoiceRowId]?.discountAmount}
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "discountAmount", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.totalExcludingVat")}
                    fullWidth
                    type="number"
                    value={row.totalWithoutVAT}
                    errorMessage={
                      fieldErrors[row.invoiceRowId]?.totalWithoutVAT
                    }
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "totalWithoutVAT", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
                <Col lg={6} style={{ padding: "8px 16px" }}>
                  <TextField
                    label={t("invoices.vatPercentage")}
                    fullWidth
                    type="number"
                    value={row.vatPercentage}
                    errorMessage={fieldErrors[row.invoiceRowId]?.vatPercentage}
                    onChange={(value) =>
                      onEditRowField(row.invoiceRowId, "vatPercentage", value)
                    }
                    disabled={!isEditable}
                  />
                </Col>
              </Row>
            </InvoiceRowContentContainer>
            <HorizontalSpacer spacing={1} />
          </div>
        ))}

        <HorizontalSpacer spacing={1} />
        <Row nogutter justify="end">
          <CircleButton
            icon={<PlusIcon />}
            onClick={onClickAddNewRow}
            disabled={!isEditable}
          />
        </Row>
      </SlideOutHeight>

      {addVatToAllFieldsModalOpen && (
        <Modal
          closeIcon
          onClose={() => setAddVatToAllFieldsModalOpen(false)}
          title={t("invoices.addVatToAllRows")}
          width={400}
          actionButtons={{
            defaultButton: {
              buttonTitle: t("common.cancel"),
              onClick: () => setAddVatToAllFieldsModalOpen(false),
            },
            actionButton: {
              buttonTitle: t("common.save"),
              onClick: onUpdateVatOnAllRows,
            },
          }}
        >
          <div>
            <BodySmall>
              {t("invoiceValidation.addingVatToAllRowsDescription")}
            </BodySmall>
            <HorizontalSpacer spacing={2} />
            <TextField
              label={t("invoiceValidation.vatAmountInPercentage")}
              value={newVatPercentage}
              name="vatPercentage"
              fullWidth
              type="number"
              onChange={(value) => {
                setNewVatPercentage(Number(value));
              }}
            />
          </div>
        </Modal>
      )}

      {deleteSuccesful && (
        <InvoiceRowDeletedTag>
          <Small fontweight={400} color={theme.colors.success}>
            {t("invoices.rowDeleted")}
          </Small>
          <CheckmarkIconSmall color={theme.colors.success} />
        </InvoiceRowDeletedTag>
      )}
    </div>
  );
};

export default InvoiceRowsDetailsSection;
